import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { SelectDropdown, TextAreaInput, TextInput } from "../../reusables/FormElements";

import { IProgramData, IProgramFormProps, ProgramField } from "../../../interfaces/forms";
import { ISelectDropdownOption } from "../../../interfaces/form_elements";
import { TLogicalOperator, TProgramCost, TProgramDifficulty, TProgramTimeframe } from "../../../interfaces/types";

import { OPBooleanOptions, OPLogicalOperators, OPProgramCost, OPProgramDifficulty, OPProgramTimeframe } from "../../../data/options";

import useSaveProgram from "../../../hooks/programs/useSaveProgram";
import useDeleteProgram from "../../../hooks/programs/useDeleteProgram";

import { closeModal, openModal } from "../../../store/slices/modal";

const ProgramForm = (props: IProgramFormProps) => {
    const { programData, selected, setSelected, setExpanded } = props;

    const dispatch = useDispatch();

    const requiredFields: ProgramField[] = ["active", "name", "method", "difficulty", "cost", "timeframe"];
    const selectFields: ProgramField[] = ["active", "method", "difficulty", "cost", "timeframe"];

    const [isProcessing, setIsProcessing] = useState(false);
    const [formData, setFormData] = useState<IProgramData>({
        id: null,
        active: { value: { value: "", label: "" }, error: "" },
        name: { value: "", error: "" },
        subtitle: { value: "", error: "" },
        description: { value: "", error: "" },
        gov_url: { value: "", error: "" },
        method: { value: { value: "", label: "" }, error: "" },
        difficulty: { value: { value: "", label: "" }, error: "" },
        cost: { value: { value: "", label: "" }, error: "" },
        timeframe: { value: { value: "", label: "" }, error: "" },
    });

    const { mutate: saveProgram } = useSaveProgram();
    const { mutate: deleteProgram } = useDeleteProgram();

    useEffect(() => {
        if (programData) {
            const active = OPBooleanOptions.find(option => option.value === (programData.active ? 'true' : 'false'));
            const method = OPLogicalOperators.find(method => method.value === programData.method);
            const difficulty = OPProgramDifficulty.find(difficulty => difficulty.value === programData.difficulty);
            const cost = OPProgramCost.find(cost => cost.value === programData.cost);
            const timeframe = OPProgramTimeframe.find(timeframe => timeframe.value === programData.timeframe);

            setFormData({
                id: programData.id > 0 ? programData.id : null,
                active: { value: { value: active?.value ?? '', label: active?.label ?? '' }, error: '' },
                name: { value: programData.name ?? '', error: '' },
                subtitle: { value: programData.subtitle ?? '', error: '' },
                description: { value: programData.description ?? '', error: '' },
                gov_url: { value: programData.gov_url ?? '', error: '' },
                method: { value: { value: method?.value ?? '', label: method?.label ?? '' }, error: '' },
                difficulty: { value: { value: difficulty?.value ?? '', label: difficulty?.label ?? '' }, error: '' },
                cost: { value: { value: cost?.value ?? '', label: cost?.label ?? '' }, error: '' },
                timeframe: { value: { value: timeframe?.value ?? '', label: timeframe?.label ?? '' }, error: '' },
            });
        } else {
            setFormData({
                id: null,
                active: { value: { value: "", label: "" }, error: "" },
                name: { value: "", error: "" },
                subtitle: { value: "", error: "" },
                description: { value: "", error: "" },
                gov_url: { value: "", error: "" },
                method: { value: { value: "", label: "" }, error: "" },
                difficulty: { value: { value: "", label: "" }, error: "" },
                cost: { value: { value: "", label: "" }, error: "" },
                timeframe: { value: { value: "", label: "" }, error: "" },
            });
        };

        // eslint-disable-next-line
    }, [programData]);

    const onChangeText = (name: ProgramField, value: string) => {
        setFormData({
            ...formData,
            [name]: { value, error: "" }
        });
    };

    const onChangeSelect = (name: ProgramField, option: ISelectDropdownOption) => {
        setFormData(prevData => {
            return {
                ...prevData,
                [name]: { value: option, error: "" }
            };
        });
    };

    const isDisabled = () => {
        const emptyFields = requiredFields.some(field => {
            let value = "";
            if (selectFields.includes(field)) {
                const dataValue = formData[field].value as ISelectDropdownOption;
                value = dataValue.value;
            } else {
                value = formData[field].value as string;
            };
            return !value?.trim()?.length;
        });

        const isDataSame =
            formData.active.value.value === (programData?.active ? 'true' : 'false') &&
            formData.name.value === (programData?.name ?? '') &&
            formData.subtitle.value === (programData?.subtitle ?? '') &&
            formData.description.value === (programData?.description ?? '') &&
            formData.gov_url.value === (programData?.gov_url ?? '') &&
            formData.method.value.value === (programData?.method ?? '') &&
            formData.difficulty.value.value === (programData?.difficulty ?? '') &&
            formData.cost.value.value === (programData?.cost ?? '') &&
            formData.timeframe.value.value === (programData?.timeframe ?? '');

        return emptyFields || isDataSame || isProcessing;
    };

    const onSave = () => {
        if (isDisabled() || !selected) return;

        // clear all errors
        setFormData(prevData => {
            const newData = { ...prevData };
            Object.keys(newData).forEach(key => {
                if (key === "id") return;
                newData[key as ProgramField].error = "";
            });
            return newData;
        });

        setIsProcessing(true);

        saveProgram({
            payload: {
                id: formData.id,
                active: formData.active.value.value === 'true',
                name: formData.name.value,
                subtitle: formData.subtitle.value?.trim()?.length ? formData.subtitle.value : null,
                description: formData.description.value,
                gov_url: formData.gov_url.value,
                method: formData.method.value.value as TLogicalOperator,
                difficulty: formData.difficulty.value.value as TProgramDifficulty,
                cost: formData.cost.value.value as TProgramCost,
                timeframe: formData.timeframe.value.value as TProgramTimeframe,
            },
            setIsProcessing,
            selected,
            setSelected,
        });
    };

    const onDelete = () => {
        if (!selected) return;

        if (!formData.id) {
            setSelected(null);
            return;
        };

        dispatch(openModal({
            name: 'MESSAGE_MODAL',
            data: {
                view: 'CONFIRMATION',
                closeBtn: true,
                title: "Confirm delete program?",
                content: "This will also delete all associated components and functions. This action cannot be undone.",
                actionTxt: "Delete",
                action: () => {
                    setIsProcessing(true);

                    deleteProgram({
                        id: formData.id as number,
                        setIsProcessing,
                        setSelected,
                        setExpanded,
                    });

                    dispatch(closeModal('MESSAGE_MODAL'));
                },
                cancelTxt: "Cancel",
            }
        }))
    };

    return (
        <div className="immployer_admin__common_form">
            <div className="immployer_admin__common_form__inputs">
                <TextInput
                    name="name"
                    type="text"
                    max_length={100}
                    label="Program Name"
                    required={true}
                    errorMsg={formData.name.error}
                    placeholder="Enter program name"
                    onChange={(value: string) => onChangeText("name", value)}
                    value={formData.name.value}
                />
                <TextInput
                    name="subtitle"
                    type="text"
                    max_length={100}
                    label="Program Subtitle"
                    errorMsg={formData.subtitle.error}
                    placeholder="Enter program subtitle"
                    onChange={(value: string) => onChangeText("subtitle", value)}
                    value={formData.subtitle.value}
                />
                <TextAreaInput
                    name="description"
                    max_length={1000}
                    label="Program Description"
                    errorMsg={formData.description.error}
                    placeholder="Enter program description"
                    onChange={(value: string) => onChangeText("description", value)}
                    value={formData.description.value}
                />
                <TextAreaInput
                    name="gov_url"
                    max_length={1000}
                    label="Program URL"
                    errorMsg={formData.gov_url.error}
                    placeholder="Enter program url"
                    onChange={(value: string) => onChangeText("gov_url", value)}
                    value={formData.gov_url.value}
                />
                <SelectDropdown
                    name="method"
                    label="Select method"
                    required={true}
                    placeholder="-- select option --"
                    value={formData.method.value}
                    onChange={(option: ISelectDropdownOption) => onChangeSelect("method", option)}
                    errorMsg={formData.method.error}
                    options={OPLogicalOperators}
                />
                <SelectDropdown
                    name="difficulty"
                    label="Select Difficulty"
                    required={true}
                    placeholder="-- select option --"
                    value={formData.difficulty.value}
                    onChange={(option: ISelectDropdownOption) => onChangeSelect("difficulty", option)}
                    errorMsg={formData.difficulty.error}
                    options={OPProgramDifficulty}
                />
                <SelectDropdown
                    name="cost"
                    label="Select Cost"
                    required={true}
                    placeholder="-- select option --"
                    value={formData.cost.value}
                    onChange={(option: ISelectDropdownOption) => onChangeSelect("cost", option)}
                    errorMsg={formData.cost.error}
                    options={OPProgramCost}
                />
                <SelectDropdown
                    name="timeframe"
                    label="Select Timeframe"
                    required={true}
                    placeholder="-- select option --"
                    value={formData.timeframe.value}
                    onChange={(option: ISelectDropdownOption) => onChangeSelect("timeframe", option)}
                    errorMsg={formData.timeframe.error}
                    options={OPProgramTimeframe}
                />
                <SelectDropdown
                    name="active"
                    label="Is program active?"
                    required={true}
                    placeholder="-- select option --"
                    value={formData.active.value}
                    onChange={(option: ISelectDropdownOption) => onChangeSelect("active", option)}
                    errorMsg={formData.active.error}
                    options={OPBooleanOptions}
                />
            </div>
            <div className="immployer_admin__common_form__actions">
                <button
                    className={`immployer_admin__btn immployer_admin__btn__secondary ${isProcessing ? "immployer_admin__btn__secondary__disabled" : ""}`}
                    onClick={() => isProcessing ? null : onDelete()}
                >
                    {!formData.id ? 'Discard' : 'Delete'}
                </button>
                <button
                    className={`immployer_admin__btn immployer_admin__btn__primary ${isDisabled() ? "immployer_admin__btn__primary__disabled" : ""}`}
                    onClick={onSave}
                >
                    {
                        isProcessing
                            ? <i className='immployer_admin__btn_loader' />
                            : 'Save'
                    }
                </button>
            </div>
        </div>
    );
};

export default ProgramForm;